import TaskModal from './TaskModal';
import { statuses } from './filters';
import { useTasks } from './useTasks';
import { useActions } from '../../hooks';
import { formatDate } from '../../utilities';
import { useAppContext } from '../../shared';
import { deleteTask, updateTask } from '../../api';
import { Form, FormItem, TopPanel } from '../../components';
import { Dropdown, SearchBar, Table } from '../../components';
import { Slug, Button, PaginatePanel } from '../../components';
import type { ItemType } from './useTasks';

const getColor = (status: string): SlugColorType => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'failed':
      return 'danger';
    case 'completed':
      return 'success';
    default:
      return 'main';
  }
};

const Tasks = () => {
  const { meta } = useAppContext();
  const {
    nodes,
    refresh,
    filters,
    setFilters,
    reloading,
    initializing: loading,
    sort,
    setSort,
    pagination,
    setPage,
  } = useTasks();

  const {
    element,
    isOpened,
    updating,
    initialValues,
    handleClose,
    handleEdit,
    handleStartEdit,
    handleStartDelete,
  } = useActions<ItemType, ItemType, ItemType>({
    name: 'Task',
    onRefresh: refresh,

    onEdit: updateTask,
    onDelete: deleteTask,

    deleteConfig: {
      getItemLabel: (item: ItemType) => {
        return `"${item.task_type}" for ${item.contact_id}`;
      },
    },
  });

  return (
    <div className="flex flex-col gap-y-md w-full">
      {element}
      {isOpened && (
        <TaskModal
          loading={updating}
          initialValues={initialValues}
          onSumbit={handleEdit}
          onClose={handleClose}
        />
      )}
      <TopPanel>
        <Form<NonNullable<typeof filters>>
          initialValue={filters}
          onChange={setFilters}
        >
          <FormItem<NonNullable<typeof filters>> name="search" label="">
            <SearchBar />
          </FormItem>
          <FormItem<NonNullable<typeof filters>> name="status" label="">
            <Dropdown
              placeholder="Status"
              grow={false}
              options={[{ value: '', label: 'All Statuses' }, ...statuses]}
            />
          </FormItem>
          <FormItem<NonNullable<typeof filters>> name="campaignId" label="">
            <Dropdown
              placeholder="Campaign"
              grow={false}
              options={[
                { value: '', label: 'All Campaigns' },
                ...meta.campaigns,
              ]}
            />
          </FormItem>
        </Form>
        <Button icon="refresh" loading={reloading} onClick={refresh}>
          Refresh
        </Button>
      </TopPanel>
      <PaginatePanel {...pagination} setPage={setPage} />
      <div className="flex-1 overflow-y-auto">
        <Table
          data={nodes}
          loading={loading}
          sort={sort}
          onSort={setSort}
          config={{
            columns: [
              {
                header: 'Id',
                getValue: ({ id }) => (
                  <Slug text={id.split('-')[0] || ''} tooltip={id} />
                ),
              },
              {
                header: 'Type',
                sortName: 'task_type',
                cellClassName: 'text-tiny',
                getValue: ({ task_type }) => task_type,
              },
              {
                header: 'Contact',
                sortName: 'contact_id',
                cellClassName: 'text-small',
                getValue: ({ contact_id }) => contact_id,
              },
              {
                header: 'Status',
                sortName: 'status',
                getValue: ({ status }) => (
                  <Slug text={status} color={getColor(status)} />
                ),
              },
              {
                header: 'Scheduled At',
                sortName: 'scheduled_at',
                cellClassName: 'text-tiny',
                getValue: ({ scheduled_at }) => formatDate(scheduled_at),
              },
              {
                header: 'Actions',
                align: 'right',
                getValue: item => (
                  <div className="flex flex-row gap-x-sm justify-end">
                    <Button
                      icon="edit"
                      type="primary"
                      onClick={() => handleStartEdit(item)}
                    />
                    <Button
                      icon="delete"
                      type="danger"
                      onClick={() => handleStartDelete(item)}
                    />
                  </div>
                ),
              },
            ],
          }}
        />
      </div>
    </div>
  );
};

export default Tasks;
